import { AotCategory, logConstants } from '../utils/SettingsLogger';
import {
	GetSettingResponse,
	GetSettingsResponse,
	SetSettingResponse,
	SetSettingsResponse,
	InitializeResponse,
} from '../Contracts/responses';
import { StatusCode } from '../Contracts/base';
import { SettingsLogger as Logger } from '../utils/SettingsLogger';
import { SettingsNativeBridge } from './SettingsNativeBridge';
import { SettingsNativeBridgeWrapper } from './SettingsNativeBridgeWrapper';
import { SettingsNativeBridgeBase } from './SettingsNativeBridgeBase';
import { v4 as uuidv4 } from 'uuid';

// Global type declarations for native bridge properties
declare global {
	interface Window {
		settingsNative?: unknown;
		settingsBridgeInstance?: SettingsNativeBridgeBase;
	}

	interface GlobalThis {
		settingsNative?: unknown;
	}
}

export class SettingsNativeBridgeFactory {
	private static _bridge: SettingsNativeBridgeBase;

	/**
	 * Gets the appropriate Settings native bridge instance based on the platform capabilities
	 * @returns SettingsNativeBridgeBase instance (either direct native binding or wrapper)
	 */
	static getBridge(): SettingsNativeBridgeBase {
		if (!SettingsNativeBridgeFactory._bridge) {
			SettingsNativeBridgeFactory._bridge = SettingsNativeBridgeFactory.createBridge();

			// Set the bridge instance in the logger so it can write traces to native
			Logger.getInstance().setBridge(SettingsNativeBridgeFactory._bridge);

			// Register bridge instance globally for dummy callbacks in browser mode
			if (typeof window !== 'undefined') {
				window.settingsBridgeInstance = SettingsNativeBridgeFactory._bridge;

				// Wire up native callbacks to bridge instance for dummy bridge mode
				if (
					window.settingsNative &&
					(window.settingsNative as { _isDummy?: boolean })._isDummy
				) {
					const bridge = SettingsNativeBridgeFactory._bridge;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const native = window.settingsNative as any;

					// Override Native→Web callback: initialize (native calls this to send init data to web)
					const originalInitialize = native.initialize;
					native.initialize = async function (initializeParams: string) {
						try {
							// Parse JSON string parameters if needed
							const parsedParams =
								typeof initializeParams === 'string'
									? JSON.parse(initializeParams)
									: initializeParams;

							// Initialize bridge internal state
							if (bridge && parsedParams) {
								// Use bridge.initialize to properly set internal state
								try {
									await bridge.initialize(parsedParams);

									// Create success response
									const response: InitializeResponse = {
										status: 0 as StatusCode,
										data: {
											platform: parsedParams.platform,
											settingsEntrypoint: parsedParams.settingsEntrypoint,
											systemInfo: parsedParams.systemInfo,
										},
									};

									// Send completion response via onInitializeComplete callback
									bridge.onInitializeComplete(uuidv4(), response);
								} catch (error) {
									console.error('Failed to initialize bridge:', error);
								}
							}

							// Call original if it exists (for other functionality)
							if (originalInitialize && typeof originalInitialize === 'function') {
								try {
									await originalInitialize.call(native, initializeParams);
								} catch (error) {
									console.error('Error calling original initialize:', error);
								}
							}
						} catch (error) {
							console.error('Error in initialize override:', error);
						}
					};

					native.onGetSettingsComplete = function (
						id: string,
						response: GetSettingsResponse | string
					) {
						try {
							// Parse response if it's a JSON string from dummy bridge
							const parsedResponse: GetSettingsResponse =
								typeof response === 'string' ? JSON.parse(response) : response;

							// Send flat response directly without payload wrapper
							bridge.onMessageReceived({
								type: 'callback',
								id: id,
								name: 'getSettings',
								params: parsedResponse, // Send flat response directly
							});
						} catch (error) {
							console.error('Error in onGetSettingsComplete:', error);
						}
					};

					native.onGetSettingComplete = function (
						id: string,
						response: GetSettingResponse | string
					) {
						try {
							// Parse response if it's a JSON string from dummy bridge
							const parsedResponse: GetSettingResponse =
								typeof response === 'string' ? JSON.parse(response) : response;

							// Send flat response directly without payload wrapper
							bridge.onMessageReceived({
								type: 'callback',
								id: id,
								name: 'getSetting',
								params: parsedResponse, // Send flat response directly
							});
						} catch (error) {
							console.error('Error in onGetSettingComplete:', error);
						}
					};

					native.onSetSettingComplete = function (
						id: string,
						response: SetSettingResponse | string
					) {
						try {
							// Parse response if it's a JSON string from dummy bridge
							const parsedResponse: SetSettingResponse =
								typeof response === 'string' ? JSON.parse(response) : response;

							// Send flat response directly without payload wrapper
							bridge.onMessageReceived({
								type: 'callback',
								id: id,
								name: 'setSetting',
								params: parsedResponse, // Send flat response directly
							});
						} catch (error) {
							console.error('Error in onSetSettingComplete:', error);
						}
					};

					native.onSetSettingsComplete = function (
						id: string,
						response: SetSettingsResponse | string
					) {
						try {
							// Parse response if it's a JSON string from dummy bridge
							const parsedResponse: SetSettingsResponse =
								typeof response === 'string' ? JSON.parse(response) : response;

							// Send flat response directly without payload wrapper
							bridge.onMessageReceived({
								type: 'callback',
								id: id,
								name: 'setSettings',
								params: parsedResponse, // Send flat response directly
							});
						} catch (error) {
							console.error('Error in onSetSettingsComplete:', error);
						}
					};

					// Override Native→Web callback: refreshSetting (native calls this to request a UI refresh for a specific setting)
					native.refreshSetting = function (settingKey: string) {
						try {
							if (bridge) {
								bridge.refreshSetting(settingKey);
							}
						} catch (error) {
							console.error('Error in refreshSetting override:', error);
						}
					};
				}
			}
		}
		return SettingsNativeBridgeFactory._bridge;
	}

	/**
	 * Forces recreation of the bridge instance on next getBridge() call
	 * Useful for testing or when platform capabilities change
	 */
	static resetBridge(): void {
		SettingsNativeBridgeFactory._bridge = null;
	}

	private static createBridge(): SettingsNativeBridgeBase {
		try {
			// Check if we're running in browser testing mode (no native bindings)
			const isDevelopment =
				typeof window !== 'undefined' &&
				(window.location.hostname === 'localhost' ||
					window.location.hostname === '127.0.0.1');

			if (isDevelopment && !SettingsNativeBridgeFactory.hasDirectNativeBinding()) {
				Logger.getInstance().write(
					logConstants.INFO,
					'Using Settings native bridge wrapper for browser testing',
					AotCategory.Info,
					'Using Settings native bridge wrapper for browser testing'
				);
				// Use wrapper for development since it has less complex implementations
				return SettingsNativeBridgeWrapper.Instance;
			}

			// Check if direct native binding is available
			if (SettingsNativeBridgeFactory.hasDirectNativeBinding()) {
				Logger.getInstance().write(
					logConstants.INFO,
					'Using native bridge',
					AotCategory.Info,
					'Using native bridge'
				);
				return SettingsNativeBridge.Instance;
			} else {
				Logger.getInstance().write(
					logConstants.INFO,
					'Using postMessage bridge',
					AotCategory.Info,
					'Using Settings native bridge wrapper (postMessage)'
				);
				return SettingsNativeBridgeWrapper.Instance;
			}
		} catch (error) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Error creating Settings native bridge: ${error}`,
				AotCategory.Error,
				`Error creating Settings native bridge: ${error}`
			);
			// Fallback to wrapper
			return SettingsNativeBridgeWrapper.Instance;
		}
	}

	private static hasDirectNativeBinding(): boolean {
		try {
			// Try to access the settingsNative global object
			// This will be available when the native binding is directly injected
			return (
				(typeof window !== 'undefined' && typeof window.settingsNative !== 'undefined') ||
				(typeof globalThis !== 'undefined' &&
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					typeof (globalThis as any).settingsNative !== 'undefined')
			);
		} catch (error) {
			Logger.getInstance().write(
				logConstants.DEBUG,
				`Direct native binding check failed: ${error}`,
				AotCategory.Info,
				`Direct native binding check failed: ${error}`
			);
			return false;
		}
	}

	/**
	 * Checks if any form of native bridge is available
	 * @returns true if either direct binding or wrapper communication is possible
	 */
	static isNativeBridgeAvailable(): boolean {
		return (
			SettingsNativeBridgeFactory.hasDirectNativeBinding() ||
			SettingsNativeBridgeFactory.hasWrapperCapabilities()
		);
	}

	private static hasWrapperCapabilities(): boolean {
		try {
			// Check for various wrapper communication methods
			const hasWebKit =
				typeof window.webkit !== 'undefined' && !!window.webkit.messageHandlers;
			const hasAndroid = typeof window.Android !== 'undefined';
			const hasParentWindow =
				typeof window !== 'undefined' && window.parent && window.parent !== window;

			return hasWebKit || hasAndroid || hasParentWindow;
		} catch (error) {
			Logger.getInstance().write(
				logConstants.DEBUG,
				`Wrapper capabilities check failed: ${error}`,
				AotCategory.Info,
				`Wrapper capabilities check failed: ${error}`
			);
			return false;
		}
	}

	/**
	 * Gets information about the current bridge configuration
	 * @returns Object with bridge type and availability information
	 */
	static getBridgeInfo(): {
		type: 'direct' | 'wrapper' | 'none';
		isAvailable: boolean;
		capabilities: {
			directBinding: boolean;
			webKit: boolean;
			android: boolean;
			iframe: boolean;
		};
	} {
		const directBinding = SettingsNativeBridgeFactory.hasDirectNativeBinding();
		const webKit =
			typeof window.webkit !== 'undefined' && !!window.webkit.messageHandlers;
		const android = typeof window.Android !== 'undefined';
		const iframe =
			typeof window !== 'undefined' && window.parent && window.parent !== window;

		const isAvailable = directBinding || webKit || android || iframe;
		const type = directBinding ? 'direct' : isAvailable ? 'wrapper' : 'none';

		return {
			type,
			isAvailable,
			capabilities: {
				directBinding,
				webKit,
				android,
				iframe,
			},
		};
	}
}
